export interface MimeTypeConfig {
  mimeType: string;
  extension: 'm4a' | 'webm' | 'wav' | 'aac' | 'ogg' | 'mp3';
  isIosPlayable: boolean;
}

/**
 * Dynamic MIME-type negotiator.
 *
 * Prioritizes iOS AVPlayer-compatible codecs (AAC in .m4a or .mp4) so that voice memos
 * recorded in Safari play natively on iOS devices. In Chrome and other Chromium browsers
 * where audio/mp4 MediaRecorder is not available, falls back to audio/webm;codecs=opus.
 */
export function resolveOptimalAudioMimeType(): MimeTypeConfig {
  if (
    typeof window === 'undefined' ||
    typeof MediaRecorder === 'undefined' ||
    typeof MediaRecorder.isTypeSupported !== 'function'
  ) {
    return { mimeType: '', extension: 'm4a', isIosPlayable: true };
  }

  const candidates: Array<{
    mimeType: string;
    extension: MimeTypeConfig['extension'];
    isIosPlayable: boolean;
  }> = [
    // Preferred: AAC in MP4 container (Native iOS AVPlayer playback)
    { mimeType: 'audio/mp4;codecs=mp4a.40.2', extension: 'm4a', isIosPlayable: true },
    { mimeType: 'audio/mp4', extension: 'm4a', isIosPlayable: true },
    { mimeType: 'audio/aac', extension: 'aac', isIosPlayable: true },

    // Fallbacks for Chromium / Android / Firefox
    { mimeType: 'audio/webm;codecs=opus', extension: 'webm', isIosPlayable: false },
    { mimeType: 'audio/webm', extension: 'webm', isIosPlayable: false },
    { mimeType: 'audio/ogg;codecs=opus', extension: 'ogg', isIosPlayable: false },
    { mimeType: 'audio/wav', extension: 'wav', isIosPlayable: true },
  ];

  for (const candidate of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(candidate.mimeType)) {
        return candidate;
      }
    } catch {
      // Continue searching
    }
  }

  return { mimeType: '', extension: 'm4a', isIosPlayable: true };
}
